import { groupBy } from '@progress/kendo-data-query';
import { observable, action, makeObservable, reaction } from 'mobx';
import { PickingBatchStore } from 'modules/picking-batch/stores';
import { PackingSlipStore } from 'modules/packing-slip/stores';
import { localStorageHandler } from 'core/services';
import { BaasicDropdownViewStore, BaasicTableSettingsViewStore } from 'common/stores';
import { FulfillmentOrderStore, OrderDateRangeViewStore, FulfillmentOrderProcessingStore } from 'modules/order/stores';
import { ShipmentInfoStore } from 'modules/shipment-info/stores';
import { WarehouseOrganizationInfoStore } from 'modules/warehouse/stores';
import { TableFilterTemplate, TableFilterNumericTemplate, TableFilterDateTemplate } from 'themes/components';
import { LookupStore } from 'modules/lookup/stores';
import { FulfillmentChannelOrderStore } from 'modules/fulfillment-channel-order/stores';
import { ApplicationStore } from 'platform/application/stores';
import {
	HyperlinkCellData,
	HyperLinkOrderNumberCellData,
	HyperLinkTrackingNumberCellData,
	TooltipDateTimeCellData,
	DateTimeCellData,
	CarrierDeliveryStatusCellData,
	PalletCellData,
	BaseListViewStore,
	BooleanCellData,
	RateCellData,
	ShippingCostCellData,
	DatetimeFromShippingInfoCellData,
	ZoneCellData,
} from 'core/stores';
import {
	ChannelService,
	OrderService,
	FulfillmentOrderItemService,
	ProductService,
	CarrierServiceService,
	CarrierService,
	ApplicationService,
	ShipmentInfoService,
	PalletService,
	PickingBatchService,
	PackingSlipService,
	FulfillmentOrderService,
} from 'common/data';
import {
	TableFilterUtility,
	checkForColumnFilter,
	checkForColumnWidth,
	RemoveFiltersModalUtility,
	notificationError,
	isSome,
	isSeller,
	isWarehouse,
	isSuperAdminOrganization,
	createFileUrl,
	translate,
	handleVoidLabelResponseNotification,
} from 'core/utils';
import {
	lookupRouteName,
	fulfillmentOrderStatus,
	initialOrderFilteringStatuses,
	initialAdvancedSearchFilterSetup,
	onMarkAsShippedStatuses,
	onUpdateBuyShippingStatuses,
	onChangeShippingStatuses,
	onHoldStatuses,
	onPullLabelStatuses,
	channelType,
	onAssignStatuses,
	sourceOfChange,
	onNeverShippedStatuses,
	onReoptimizeStatuses,
	fulfillmentOrdersGroupByOptions,
} from 'common/constants';
import moment from 'moment';
import * as printJS from 'print-js';
import _ from 'lodash';
import {
	AssignCarrierServiceAndWarehouse,
	BulkPrint,
	BulkPrintHistory,
	BulkPrintHistoryCreate,
	ChangeShipping,
	ChangeWarehouseShipByDate,
	CloseOutExit,
	CloseOutPalletizing,
	EditOrder,
	ExitScanMode,
	ExportLabels,
	LabelingMode,
	MarkAsShipped,
	MarkAsShippedBulk,
	NeverShipped,
	OnHold,
	PalletizingMode,
	SplitFulfillmentOrder,
	Tracking,
	UpdateBuyShipping,
	VoidLabel,
} from '../pages';

class OrderListViewStore extends BaseListViewStore {
	@observable change = false;
	@observable selectedItem = null;
	@observable pdfURL = null;
	@observable showTodayShipments;
	@observable selectAll = false;
	@observable useRegularPalletizingMode;
	@observable isSelectAllCheckboxSelected = false;
	@observable page = 1;
	@observable infiniteSearchPhrase = null;
	@observable isBulkActionPopupOpen = false;
	@observable selectedItems = [];
	@observable selectedGroups = [];
	@observable showSelectAllColumn = false;
	@observable isPickingMode = false;
	@observable isBulkPrintMode = false;
	actionName = null;
	group = null;
	groupable = true;
	expandedGroups = [];
	pickingBatchStore = null;
	packingSlipStore = null;
	printPdf = false;
	hasActiveShipment = false;
	fulfillmentOrderStatus;
	reload = false;
	plainLabelVoiding = false;
	reoptimizeLabelVoiding = false;
	isBuyShipping = false;
	notProcessableItems = [];
	selectedColumns = [];
	localStorageWidthKey = 'fulfillment-orders-column-width';
	@observable bulkActionsArray = [
		{
			id: 1,
			name: 'FULFILLMENTORDER.BULK.CHANGESHIPPING',
			display: true,
			onClick: () => this.openChangeShippingModalBulk(),
		},
		{
			id: 4,
			name: 'FULFILLMENTORDER.BULK.ONHOLDREMOVEHOLD',
			display: true,
			onClick: () => this.openBulkOnHoldModal(),
		},
		{
			id: 5,
			name: 'FULFILLMENTORDER.BULK.MARKASSHIPPED',
			display: true,
			onClick: () => this.openBulkMarkAsShipped(),
		},
		{
			id: 6,
			name: 'FULFILLMENTORDER.BULK.PULLLABEL',
			display: true,
			onClick: () => this.openBulkPullLabelConfirmModal(),
		},
		{
			id: 7,
			name: 'FULFILLMENTORDER.BULK.ASSIGN',
			display: true,
			onClick: () => this.openAssignModalBulk(),
		},
		{
			id: 8,
			name: 'FULFILLMENTORDER.BULK.UPDATEBUYSHIPPING',
			display: true,
			onClick: () => this.openUpdateBuyShippingModalBulk(),
		},
		{
			id: 9,
			name: 'FULFILLMENTORDER.BULK.NEVERSHIPPED',
			display: true,
			onClick: () => this.openNeverShippedModalBulk(),
		},
		{
			id: 10,
			name: 'FULFILLMENTORDER.BULK.REOPTIMIZEORDER',
			display: true,
			onClick: () => this.openReoptimizeConfirmModalBulk(),
		},
		{
			id: 11,
			name: 'FULFILLMENTORDER.BULK.CHANGEWHSHIPBYDATE',
			display: isSuperAdminOrganization(this.rootStore),
			onClick: () => this.openChangeWarehouseShipByDateModal(),
		},
	];

	constructor(rootStore) {
		super(rootStore, {
			listStore: new FulfillmentChannelOrderStore(rootStore),
			queryParams: {
				embed: null,
				filter: localStorageHandler.get('fulfillment-orders-filter-filter'),
			},
			tableData: {
				columns: [
					{
						key: 'fulfillmentOrderId',
						title: 'ORDERVIEW.FULFILLMENTORDERID',
						dataCell: HyperlinkCellData,
						onClick: dataItem => this.openDetailPage(dataItem.id),
						propertyToCheck: 'onHoldReasonId',
						translation: 'ORDERVIEW.ONHOLD',
						selected: true,
						width: 200,
						filter: null,
						locked: true,
						sortable: true,
						filterType: TableFilterNumericTemplate,
						dontSplit: true,
						isFoListFilter: true,
					},
					{
						key: 'fulfillmentOrderStatus',
						title: 'ORDERVIEW.STATUS',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						lookupConstant: lookupRouteName.fulfillmentOrderStatus,
						filterApiParams: {
							bulkPrintMode: false,
						},
					},
					{
						key: 'orderNumber',
						title: 'ORDERVIEW.ORDERNUMBER',
						service: 'orderService',
						selected: false,
						dataCell: HyperLinkOrderNumberCellData,
						width: 200,
						filter: null,
						sortable: true,
						filterType: TableFilterTemplate,
						dontSplit: true,
						isWarehouse: isWarehouse(rootStore),
						filterApiParams: {
							isWarehouse: isWarehouse(rootStore),
							getOnlySplitted: true,
							checkPartiallyFulfillMixedOrders: true,
						},
					},
					{
						key: 'orderDateTime',
						title: 'ORDERVIEW.ORDERDATE',
						selected: false,
						dataCell: DateTimeCellData,
						dateFormat: 'LLL',
						useLocalTime: true,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'fulfillmentOrderShipByLocalDate',
						title: 'ORDERVIEW.SHIPBYDATE',
						dataCell: TooltipDateTimeCellData,
						dateFormat: 'L',
						newPropertyToUse: 'fulfillmentOrderNewShipByDate',
						translationKey: 'TOOLTIP.NEWSHIPBYDATE',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						dontSplit: true,
					},
					{
						key: 'whShipByChannelDate',
						title: 'ORDERVIEW.WHSHIPBYDATE',
						dataCell: TooltipDateTimeCellData,
						dateFormat: 'L',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						dontSplit: true,
					},
					{
						key: 'fulfillmentOrderDeliverByLocalDate',
						title: 'ORDERVIEW.DELIVERBYDATE',
						dataCell: TooltipDateTimeCellData,
						dateFormat: 'L',
						newPropertyToUse: 'fulfillmentOrderNewDeliverByDate',
						translationKey: 'TOOLTIP.NEWDELIVERBYDATE',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						dontSplit: true,
					},
					{
						key: 'requiredCarrierService',
						title: 'ORDERVIEW.REQUIREDCARRIERSERVICE',
						service: 'orderService',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						getDistinctValues: true,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'shippingCity',
						title: 'ORDERVIEW.SHIPPINGADDRESSCITY',
						service: 'fulfillmentOrderService',
						selected: false,
						width: 200,
						getDistinctValues: true,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
						filterApiParams: {
							isWarehouse: isWarehouse(rootStore),
						},
					},
					{
						key: 'productQuantity',
						title: 'ORDERVIEW.QUANTITY',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterNumericTemplate,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'productQuantityPerSku',
						title: 'ORDERVIEW.PRODUCTSKU',
						service: 'productService',
						selected: false,
						dataCell: HyperlinkCellData,
						onClick: dataItem => this.openProductPage(dataItem),
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						filterKey: 'mainSku',
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'shippingZipCode',
						title: 'ORDERVIEW.SHIPPINGADDRESSZIPCODE',
						service: 'fulfillmentOrderService',
						selected: false,
						width: 200,
						getDistinctValues: true,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
						filterApiParams: {
							isWarehouse: isWarehouse(rootStore),
						},
					},
					{
						key: 'shippingState',
						title: 'ORDERVIEW.STATE',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						lookupConstant: lookupRouteName.state,
						dontSplit: true,
					},
					{
						key: 'orderChannel',
						title: 'ORDERVIEW.CHANNEL',
						service: 'channelService',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						dontSplit: true,
						filterKey: 'name',
						enabled: !isWarehouse(rootStore),
					},
					{
						key: 'orderChannelType',
						title: 'ORDERVIEW.CHANNELTYPE',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						lookupConstant: lookupRouteName.channelType,
						enabled: !isWarehouse(rootStore),
					},
					{
						key: 'orderType',
						title: 'ORDERVIEW.ORDERTYPE',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						lookupConstant: lookupRouteName.orderType,
						enabled: !isWarehouse(rootStore),
					},
					{
						key: 'orderConfirmationType',
						title: 'ORDERVIEW.CONFIRMATIONTYPE',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						lookupConstant: lookupRouteName.confirmationType,
					},
					{
						key: 'carrierService',
						title: 'ORDERVIEW.ASSIGNEDCARRIERSERVICE',
						service: 'carrierServiceService',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						filterKey: 'name',
						dontSplit: true,
					},
					{
						key: 'customerServiceOrderNumber',
						title: 'ORDERVIEW.CUSTOMERSERVICEORDERNUMBER',
						service: 'orderService',
						sortable: true,
						selected: false,
						getDistinctValues: true,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						dontSplit: true,
					},
					{
						key: 'source',
						title: 'ORDERVIEW.CHANNELSOURCE',
						service: 'orderService',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						dontSplit: true,
						getDistinctValues: true,
					},
					{
						key: 'onHoldReason',
						title: 'ORDERVIEW.ONHOLDREASON',
						showUserFilter: false,
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						lookupConstant: lookupRouteName.onHoldReason,
						filterApiParams: {
							showUserFilter: true,
						},
					},
					{
						key: 'replacementReason',
						title: 'ORDERVIEW.REPLACEMENTREASON',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						lookupConstant: lookupRouteName.replacementReason,
					},
					{
						key: 'seller',
						title: 'ORDERVIEW.SELLER',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						filterKey: 'applicationTitle',
						service: 'applicationService',
						enabled: isWarehouse(rootStore) || isSuperAdminOrganization(rootStore),
						filterApiParams: {
							sellerOnly: true,
						},
					},
					{
						key: 'warehouse',
						title: 'ORDERVIEW.ASSIGNEDWAREHOUSE',
						service: 'applicationService',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: isSeller(rootStore) || isSuperAdminOrganization(rootStore) ? TableFilterTemplate : null,
						filterKey: 'applicationTitle',
						dontSplit: true,
						filterApiParams: {
							warehouseOnly: true,
						},
					},
					{
						key: 'shippingCost',
						title: 'ORDERVIEW.SHIPPINGCOST',
						dataCell: ShippingCostCellData,
						service: 'shipmentInfoService',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						enabled: !isWarehouse(rootStore),
						filterType: TableFilterNumericTemplate,
					},
					{
						key: 'trackingNumbers',
						title: 'ORDERVIEW.TRACKINGNUMBER',
						dataCell: HyperLinkTrackingNumberCellData,
						shipmentStatusPath: 'shipmentInfoStatus',
						carrierTypePath: 'carrierType',
						service: 'shipmentInfoService',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						filterKey: 'trackingNumber',
						dontSplit: true,
					},
					{
						key: 'shippingDateTime',
						title: 'ORDERVIEW.SHIPPEDDATE',
						dataCell: DatetimeFromShippingInfoCellData,
						dateFormat: 'LLL',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						dontSplit: true,
						useLocalTime: true,
					},
					{
						key: 'primeOrder',
						title: 'ORDERVIEW.ISPRIME',
						dataCell: BooleanCellData,
						sortable: true,
						selected: false,
						width: 120,
						filter: null,
						filterType: TableFilterTemplate,
					},
					{
						key: 'isBuyShipping',
						title: 'ORDERVIEW.ISBUYSHIPPING',
						dataCell: BooleanCellData,
						sortable: true,
						selected: false,
						width: 120,
						filter: null,
						filterType: TableFilterTemplate,
					},
					{
						key: 'deliveryReplacement',
						title: 'ORDERVIEW.ISREPLACEMENT',
						dataCell: BooleanCellData,
						service: 'orderService',
						sortable: true,
						selected: false,
						width: 120,
						filter: null,
						filterType: TableFilterTemplate,
					},
					{
						key: 'assignedRate',
						title: 'ORDERVIEW.ASSIGNEDRATE',
						dataCell: RateCellData,
						service: 'orderService',
						sortable: true,
						selected: false,
						width: 200,
						filterType: TableFilterNumericTemplate,
						filter: null,
						enabled: !isWarehouse(rootStore),
					},
					{
						key: 'pallet',
						title: 'ORDERVIEW.PALLETNAME',
						dataCell: PalletCellData,
						service: 'palletService',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						filterKey: 'name',
						dontSplit: true,
						filterApiParams: {
							containsOrders: true,
						},
					},
					{
						key: 'deliveryStatus',
						title: 'ORDERVIEW.CARRIERDELIVERYSTATUS',
						dataCell: CarrierDeliveryStatusCellData,
						service: 'shipmentInfoService',
						isClickable: false,
						sortable: true,
						lookupConstant: lookupRouteName.carrierDeliveryStatus,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						filterKey: 'name',
						dontSplit: true,
					},
					{
						key: 'pickingBatchNumber',
						title: 'FULFILLMENTORDER.PICKINGBATCH.NAME',
						service: 'pickingBatchService',
						isClickable: false,
						sortable: true,
						selected: false,
						filterType: TableFilterNumericTemplate,
						width: 200,
						filter: null,
						dontSplit: true,
					},
					{
						key: 'referenceNumber',
						title: 'ORDERVIEW.REFERENCENUMBER',
						service: 'orderService',
						selected: false,
						filterKey: 'partnerPO',
						getDistinctValues: true,
						filterType: TableFilterTemplate,
						width: 200,
						filter: null,
						isClickable: true,
						sortable: true,
						dontSplit: true,
						filterApiParams: {
							isWarehouse: isWarehouse(rootStore),
						},
					},
					{
						key: 'internalNote',
						title: 'ORDERVIEW.NOTE',
						selected: false,
						width: 200,
						filter: null,
						isClickable: true,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'zone',
						title: 'ORDERVIEW.ZONE',
						dataCell: ZoneCellData,
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						filterType: TableFilterNumericTemplate,
					},
					{
						key: 'carrier',
						title: 'ORDERVIEW.ASSIGNEDCARRIER',
						service: 'carrierService',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						filterKey: 'name',
					},
				],
				actions: {
					onAssign: async item =>
						this.openModal({
							...this.assignProps,
							data: { selectedItemId: item.id },
						}),
					onHold: async item =>
						this.openModal({
							onSuccess: async () => await this.findFulfillmentOrders(),
							data: { selectedItemId: item.id },
							component: OnHold,
						}),
					onCancel: async item => this.openConfirmationModal(this.getCancelConfirmModalProps(item)),
					onChangeShipping: async item =>
						this.openModal({
							onSuccess: async () => await this.queryUtility.initialize(false),
							data: { selectedItemId: item.id },
							component: ChangeShipping,
						}),
					onEditOrder: async item => this.openModal(this.getEditOrderModalProps(item)),
					onPrintLabel: async item => this.getLabelAndPrint(item),
					onPrintPackingSlip: async item => this.printPackingSlip(item),
					onPullLabel: async item => this.openConfirmationModal(this.getPullLabelConfirmModalProps(item)),
					onNeverShipped: async item =>
						this.openModal({
							...this.neverShippedProps,
							data: { selectedItem: item },
						}),
					onMarkAsShipped: async item =>
						this.openModal({
							onSuccess: async () => await this.findFulfillmentOrders(),
							data: { selectedItemId: item.id },
							component: MarkAsShipped,
						}),
					onVoidLabel: async item => this.openVoidLabelConfirmModal(item),
					onSplitFulfillmentOrder: async item => this.openSplitFulfillmentOrderModal(item),
					onReoptimize: async item => this.openConfirmationModal(this.getReoptimizeConfirmModalProps(item)),
				},
				sortObject: [
					{
						field: 'fulfillmentOrderId',
						dir: 'asc',
					},
				],
			},
			routes: {
				list: () => this.rootStore.routerStore.goTo('master.app.main.order.list', {}),
			},
			permissions: {
				onAssign: () => this.authStore.hasPermission('fulfillmentOrderSection.update'),
				onHold: () => this.authStore.hasPermission('fulfillmentOrderSection.full'),
				onCancel: () => this.authStore.hasPermission('warehouseSection.full'),
				onChangeShipping: () => this.authStore.hasPermission('fulfillmentOrderSection.full'),
				onEditOrder: () => this.authStore.hasPermission('fulfillmentOrderSection.update'),
				onPrintLabel: () => this.authStore.hasPermission('warehouseSection.read'),
				onPrintPackingSlip: () => this.authStore.hasPermission('warehouseSection.read'),
				onPullLabel: () => this.authStore.hasPermission('fulfillmentOrderSection.full'),
				onNeverShipped: () => this.authStore.hasPermission('fulfillmentOrderSection.update'),
				onMarkAsShipped: () => isSeller(rootStore) || isSuperAdminOrganization(rootStore),
				onVoidLabel: () => this.authStore.hasPermission('warehouseSection.read'),
				onSplitFulfillmentOrder: () => this.authStore.hasPermission('fulfillmentOrderSection.full'),
				onReoptimize: () => isSuperAdminOrganization(rootStore),
			},
		});
		makeObservable(this);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		reaction(
			() => this.isPickingMode || this.isBulkPrintMode,
			() => {
				this.showSelectAllColumn =
					this.isSeller || this.isSuperAdminOrganization || this.isPickingMode || this.isBulkPrintMode;
			}
		);
		let promises = [];
		if (isSuperAdminOrganization(this.rootStore) || isWarehouse(this.rootStore)) {
			promises.push(this.rootStore.viewStore.handleSellerForChildViewStore(this));
		}
		this.groupByDropdownStore = new BaasicDropdownViewStore();
		promises.push(
			this.groupByDropdownStore.initialize({
				staticOptions: fulfillmentOrdersGroupByOptions,
				value: localStorageHandler.get('groupByOption'),
				onChangeCallback: value => this.onGroupBySelect(value),
			})
		);
		this.warehouseDropdownStore = new BaasicDropdownViewStore();
		promises.push(
			this.warehouseDropdownStore.initialize({
				store: new ApplicationStore(this.rootStore),
				itemTemplate: {
					value: 'id',
					label: 'applicationTitle',
				},
				fetchInitialValues: false,
				filterProperties: {
					warehouseOnly: true,
					isOrganizationActive: true,
					embed: ['warehouseOrganizationInfo'],
				},
				onChangeCallback: value => this.onWarehouseSelect(value),
			})
		);
		await Promise.all(promises);

		await this.queryUtility.initialize(this.findFulfillmentOrders);
		this.shouldSetInitialFiltering();
		checkForColumnFilter(localStorageHandler.get('fulfillment-orders-filter-table-data'), this.tableData.columns);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.columnFilterSetup = 'fulfillment-orders-table-data';
		this.lookupStore = new LookupStore(this.rootStore);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(this.rootStore);
		this.warehouseOrganizationInfoStore = new WarehouseOrganizationInfoStore(this.rootStore);
		this.shipmentInfoStore = new ShipmentInfoStore(this.rootStore);
		this.removeFiltersModal = new RemoveFiltersModalUtility({ viewStore: this });
		this.organizationStore = new ApplicationStore(this.rootStore);
		this.orderDateRangeViewStore = new OrderDateRangeViewStore({
			queryUtility: this.queryUtility,
			findFunction: this.findFulfillmentOrders,
			localStorageHandlerKey: 'fo-date-filter-option',
		});
		this.hasPiiAccess = this.rootStore.authStore.hasPiiAccess;
		this.tableFilter = new TableFilterUtility({
			lookupStore: this.lookupStore,
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			services: [
				new FulfillmentOrderService(this.rootStore.app.baasic.apiClient),
				new ChannelService(this.rootStore.app.baasic.apiClient),
				new OrderService(this.rootStore.app.baasic.apiClient),
				new FulfillmentOrderItemService(this.rootStore.app.baasic.apiClient),
				new ProductService(this.rootStore.app.baasic.apiClient),
				new CarrierServiceService(this.rootStore.app.baasic.apiClient),
				new CarrierService(this.rootStore.app.baasic.apiClient),
				new ApplicationService(this.rootStore.app.baasic.apiClient),
				new ShipmentInfoService(this.rootStore.app.baasic.apiClient),
				new PalletService(this.rootStore.app.baasic.apiClient),
				new PickingBatchService(this.rootStore.app.baasic.apiClient),
				new PackingSlipService(this.rootStore.app.baasic.apiClient),
			],
		});
		this.initialTableData = _.cloneDeep(this.tableData);
		this.isSeller = isSeller(this.rootStore);
		this.isSuperAdminOrganization = isSuperAdminOrganization(this.rootStore);
		this.showSelectAllColumn = this.isSeller || this.isSuperAdminOrganization;
		this.organizationStore = new ApplicationStore(this.rootStore);
		this.pickingBatchStore = new PickingBatchStore(this.rootStore);
		this.packingSlipStore = new PackingSlipStore(this.rootStore);
		if (!this.isSeller && !this.isSuperAdminOrganization) {
			this.getOrganizationInfo();
		} else {
			this.useRegularPalletizingMode = this.isSeller || this.isSuperAdminOrganization;
			this.setModes();
		}
		this.setButtons();
		this.setSearchVisible();
		this.getFulfillmentOrderStatuses();
		this.resume();
	};

	@action.bound
	shouldSetInitialFiltering = async () => {
		const currentFiltering = localStorageHandler.get('fulfillment-orders-filter-filter');
		if (currentFiltering != null) {
			currentFiltering.sellerIds = this.queryUtility.filter.sellerIds;
			this.queryUtility.filter = currentFiltering;
			this.showTodayShipments = currentFiltering.showTodayShipments;
		} else {
			this.setInitialColumnsFiltering();
			this.showTodayShipments = true;
		}
	};

	@action.bound
	setInitialColumnsFiltering = async () => {
		let orderStatusColumn = _.find(this.tableData.columns, column => column.key === 'fulfillmentOrderStatus');
		if (orderStatusColumn) {
			orderStatusColumn.filter = {
				selected: initialOrderFilteringStatuses,
				freeType: '',
				specificDate: null,
				dateRangeStart: null,
				dateRangeEnd: null,
				specificNumber: null,
				numberRangeStart: null,
				numberRangeEnd: null,
			};
		}
		this.queryUtility.filter.advancedSearch = initialAdvancedSearchFilterSetup;
	};

	@action.bound
	pullLabel = async item => {
		this.suspend();
		try {
			const response = await new FulfillmentOrderProcessingStore(this.rootStore).createFulfillmentShipment(
				item.id,
				sourceOfChange.pullLabelOrderList,
				false
			);
			if (response.shipmentInfo) {
				this.rootStore.notificationStore.success(['ORDERVIEW.PULLLABELSUCCESS', { id: item.fulfillmentOrderId }]);
			} else if (response.message || response.errors) {
				// Error notification after shipment PII data is validated as false on backend, before create shipment request.
				this.rootStore.notificationStore.error([
					response.message ? response.message : 'ORDERVIEW.PULLLABELERROR',
					{ id: item.fulfillmentOrderId, notification: response.errors ? response.errors.join(' ') : null },
				]);
			} else if (response.shipmentData.notifications) {
				// Error notification after shipment data is validated as true, but error occured on create shipment request.
				this.rootStore.notificationStore.error([
					(response.message = 'ORDERVIEW.PULLLABELERROR'),
					{ id: item.fulfillmentOrderId, notification: response.shipmentData.notifications.join(' ') },
				]);
			} else {
				// Generic error notification if no specific error message has been caught.
				this.rootStore.notificationStore.error(['ORDERVIEW.PULLLABELERROR', { id: item.fulfillmentOrderId }]);
			}
		} catch (err) {
			this.rootStore.notificationStore.error([
				err.data.message ? err.data.message : 'ORDERVIEW.PULLLABELERROR',
				{ id: item.fulfillmentOrderId },
			]);
		}
		await this.queryUtility.initialize(false);
		this.resume();
	};

	@action.bound
	pullLabelBulk = async items => {
		this.suspend();
		try {
			let selectedItems = [];
			items.map(selectedItem => {
				let index = _.findIndex(onPullLabelStatuses, item => item == selectedItem.fulfillmentOrderStatusAbrv);
				if (index != -1) {
					// If selected order has active label.
					let hasActiveLabel =
						isSome(selectedItem.activeShipmentCount) &&
						selectedItem.activeShipmentCount > 0 &&
						isSome(selectedItem.label);
					if (!hasActiveLabel) {
						selectedItems.push(selectedItem);
					} else {
						this.notProcessableItems.push(selectedItem);
					}
				} else {
					this.notProcessableItems.push(selectedItem);
				}
			});
			if (selectedItems.length > 0) {
				this.resume();
				let productionModeOrderSelected = false;
				if (
					process.env.NODE_ENV === 'staging' ||
					process.env.NODE_ENV == 'test' ||
					process.env.NODE_ENV == 'development'
				) {
					const productionModeOrder = _.find(selectedItems, item => {
						return item.productionMode == true;
					});
					if (productionModeOrder != null) {
						productionModeOrderSelected = true;
					}
				}
				if (!productionModeOrderSelected) {
					this.resetSelectedItems();
					const fulfillmentOrderIds = _.map(selectedItems, item => {
						return item.id;
					});
					const notificationId = this.rootStore.notificationStore.load(translate('BULKNOTIFICATION.PULLLABEL'));
					var response = await new FulfillmentOrderProcessingStore(this.rootStore).createFulfillmentShipmentBulk(
						{ fulfillmentOrderIds: fulfillmentOrderIds },
						sourceOfChange.pullLabelBulkOrderList,
						false
					);
					this.rootStore.notificationStore.dismiss(notificationId);

					// Notifications.
					if (response.failedCount == 0 && response.successCount > 0) {
						// If all is success.
						this.rootStore.notificationStore.success([
							'ORDERVIEW.BULKPULLLABELRESPONSESUCCESS',
							{ successful: response.successful },
						]);
					} else if (response.failedCount > 0 && response.successCount > 0) {
						// If errors occured.
						this.rootStore.notificationStore.success([
							'ORDERVIEW.BULKPULLLABELRESPONSEPARTIALSUCCESS',
							{ successful: response.successCount, failed: response.failedCount },
						]);
					} else {
						// If all is fail.
						this.rootStore.notificationStore.error(['ORDERVIEW.BULKPULLLABELRESPONSEFAIL']);
					}
				} else {
					this.rootStore.notificationStore.error(['ORDERVIEW.BULKPULLLABELPRODUCTIONMODE']);
				}
			}

			// Notify how many orders are not processable.
			if (this.notProcessableItems.length > 0) {
				this.rootStore.notificationStore.warning([
					'ORDERVIEW.NOTPROCESSABLEORDERS',
					{ count: this.notProcessableItems.length },
				]);
			}

			// Reset selection.
			this.selectedItems = [];
			this.notProcessableItems = [];
			if (this.groupByDropdownStore.value) {
				this.selectedGroups = [];
			}
			this.reload = true;
			await this.queryUtility.initialize(false);
		} catch (err) {
			this.rootStore.notificationStore.error(['ORDERVIEW.BULKPULLLABELERROR']);
		}
	};

	@action.bound
	getFulfillmentOrderStatuses = async () => {
		await this.lookupStore.find({ rpp: 50, page: 1 }, lookupRouteName.fulfillmentOrderStatus);
	};

	@action.bound
	getOrganizationInfo = async () => {
		await this.organizationStore.getOrganizationInfo();
		this.useRegularPalletizingMode = this.organizationStore.item.warehouseOrganizationInfo.useRegularPalletizingMode;
		this.setModes();
	};

	@action.bound
	getLabelAndPrint = async item => {
		try {
			if (isSome(item.activeShipmentCount) && item.activeShipmentCount > 0) {
				await this.shipmentInfoStore.reprintLabel(item.activeShipmentInfoIds, true);
				if (this.shipmentInfoStore.item.data.hasOwnProperty('label')) {
					this.pdfURL = createFileUrl(this.shipmentInfoStore.item.data.label);
					this.printPdf = true;
					this.findFulfillmentOrders(true);
				} else {
					this.rootStore.notificationStore.error('FULFILLMENTORDER.LABELNOTFOUND');
				}
			} else {
				this.rootStore.notificationStore.error('FULFILLMENTORDER.LABELNOTFOUND');
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	};

	@action.bound
	printPackingSlip = async item => {
		this.suspend();
		try {
			await this.packingSlipStore.generatePdf(item.id);
			const pdfResponse = this.packingSlipStore.response;
			if (!pdfResponse.printedWithPrintNode) {
				const fileURL = createFileUrl(pdfResponse.packingSlip);
				printJS(fileURL);
			}
			this.rootStore.notificationStore.success('FULFILLMENTORDER.PACKINGSLIP.SUCCESS');
			this.findFulfillmentOrders();
		} catch (err) {
			this.rootStore.notificationStore.error(['FULFILLMENTORDER.PACKINGSLIP.ERROR']);
		} finally {
			this.resume();
		}
	};

	@action.bound
	reoptimizeOrder = async (item, voidOnChannel = false) => {
		this.suspend();
		try {
			await this.fulfillmentOrderStore.reoptimize(
				{
					fulfillmentOrderIds: [item.id],
					voidOnChannel: voidOnChannel,
				},
				sourceOfChange.reoptimizeOrderList
			);
			this.rootStore.notificationStore.success(['ORDERVIEW.REOPTIMIZEORDERSUCCESS', { id: item.fulfillmentOrderId }]);
			this.reload = true;
			await this.queryUtility.initialize(false);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	reoptimizeOrderBulk = async (items, voidOnChannel = false) => {
		this.suspend();
		try {
			await new Promise(resolve => setTimeout(resolve, 5000));
			await this.fulfillmentOrderStore.reoptimize(
				{
					fulfillmentOrderIds: _.map(items, i => {
						return i.id;
					}),
					voidOnChannel: voidOnChannel,
				},
				sourceOfChange.reoptimizeOrderBulk
			);
			this.reload = true;
			await this.queryUtility.initialize(false);
			this.rootStore.notificationStore.success(['ORDERVIEW.REOPTIMIZEORDERBULKSUCCESS', { count: items.length }]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	openDetailPage = id => {
		this.rootStore.routerStore.goTo('master.app.main.order.list.detail', {
			id: id,
		});
	};

	openProductPage = productId => {
		this.rootStore.routerStore.goTo('master.app.main.product.list.detail', {
			id: productId,
		});
	};

	@action.bound
	setModes = () => {
		let modesArray = [
			{
				id: 1,
				name: 'FULFILLMENTORDER.MODE.REGULAR',
				onClick: () => this.setRegularMode(),
			},
			{
				id: 2,
				name: 'FULFILLMENTORDER.MODE.PICKING',
				onClick: () => this.setPickingMode(),
			},
			{
				id: 3,
				name: 'FULFILLMENTORDER.MODE.LABELING',
				onClick: () =>
					this.openModal({
						onSuccess: async () => await this.findFulfillmentOrders(),
						data: { printLabelCallback: this.findFulfillmentOrders },
						component: LabelingMode,
						className: 'ReactModal--lrg',
					}),
			},
		];
		if (this.useRegularPalletizingMode) {
			modesArray = [
				...modesArray,
				{
					id: 4,
					name: 'FULFILLMENTORDER.MODE.PALLETIZING',
					onClick: () =>
						this.openModal({
							data: { palletizeCallback: this.findFulfillmentOrders },
							component: PalletizingMode,
							className: 'ReactModal--lrg',
						}),
				},
				{
					id: 5,
					name: 'FULFILLMENTORDER.MODE.EXITSCAN',
					onClick: () => this.openModal(this.exitScanProps),
				},
			];
		} else {
			modesArray = [
				...modesArray,
				{
					id: 5,
					name: 'FULFILLMENTORDER.MODE.EXITSCAN',
					onClick: () => this.openModal(this.exitScanProps),
				},
			];
		}
		this.setHeaderModes(modesArray);
	};

	exitScanProps = {
		data: { exitScanCallback: async () => await this.findFulfillmentOrders() },
		component: ExitScanMode,
		className: 'ReactModal--lrg',
	};

	@action.bound
	setAsChanged() {
		this.change = !this.change;
	}

	@action.bound
	setButtons = async () => {
		const buttons = {
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-cog spc--right--sml',
					onClick: this.baasicTableSettingsViewStore.openTableSettingsModal,
					text: 'SETTINGS',
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-trash spc--right--sml',
					onClick: () => {
						this.removeFiltersModal.handleRemoveFiltersButtonClick();
					},
					text: 'FILTER.REMOVEALL',
					disabled: !!(this.queryUtility.filter.advancedSearch === undefined),
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-truck-moving spc--right--sml',
					authorization: 'shippingCarrierSection.full',
					onClick: () => this.openModal({ component: Tracking }),
					text: 'FULFILLMENTORDER.TRACKING',
				},
			],
		};
		if (this.hasPiiAccess) {
			buttons.buttons.push({
				className: 'btn btn--dropdown header__button',
				iconClassName: 'fa fa-file-download spc--right--sml',
				authorization: 'fulfillmentOrderSection.full',
				onClick: () => this.openModal({ component: ExportLabels }),
				text: 'FULFILLMENTORDER.EXPORTLABELSFORTODAY',
			});
		}
		if (this.isBulkPrintMode) {
			buttons.buttons.push({
				className: 'btn btn--dropdown header__button',
				iconClassName: 'fa fa-history spc--right--sml',
				authorization: 'bulkPrintSection.full',
				onClick: () =>
					this.openModal({
						data: { printHistoryCallback: this.bulkPrintHistoryCallback },
						component: BulkPrintHistory,
						className: 'ReactModal--lrg',
					}),
				text: 'FULFILLMENTORDER.BULKPRINT.HISTORY',
			});
			buttons.buttons.push({
				className: 'btn btn--dropdown header__button',
				iconClassName: 'fa fa-sign-out-alt spc--right--sml',
				authorization: 'bulkPrintSection.full',
				onClick: () => this.setBulkPrintMode(false),
				text: 'FULFILLMENTORDER.EXITBULKPRINT',
			});
			if (this.selectedItems.length != 0 || true) {
				buttons.buttons.push({
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-print spc--right--sml',
					authorization: 'bulkPrintSection.full',
					onClick: () => {
						if (this.selectedItems.length != 0) {
							this.openModal({
								onSuccess: async ({ openPrintWindow, bulkPrintHistory, pdfURL }) => {
									this.pdfURL = pdfURL;
									this.selectedItems = [];
									this.selectedGroups = [];
									this.printPdf = true;
									await this.findFulfillmentOrders(openPrintWindow);
									this.openModal({
										onSuccess: async () => await this.queryUtility.initialize(false),
										data: bulkPrintHistory,
										component: BulkPrintHistoryCreate,
									});
								},
								data: this.selectedItems,
								component: BulkPrint,
								className: 'ReactModal--bulk-print',
							});
						} else {
							this.rootStore.notificationStore.error('ORDERVIEW.NOITEMSSELECTED');
						}
					},
					text: 'FULFILLMENTORDER.PRINTBULKLIST',
				});
			}
		}
		if (this.isPickingMode) {
			buttons.buttons.push({
				className: 'btn btn--dropdown header__button',
				iconClassName: 'fa fa-truck-pickup spc--right--sml',
				onClick: this.createPickingBatch,
				text: 'FULFILLMENTORDER.PICKINGBATCH.TITLE',
			});
		}
		if (!this.isPickingMode && !this.isBulkPrintMode) {
			if (this.hasPiiAccess) {
				buttons.buttons.push({
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-print spc--right--sml',
					authorization: 'bulkPrintSection.full',
					onClick: () => this.setBulkPrintMode(true),
					text: 'FULFILLMENTORDER.BULKPRINT.TITLE',
				});
			}
			if (this.isSuperAdminOrganization) {
				buttons.buttons.push(
					{
						className: 'btn btn--dropdown header__button',
						iconClassName: 'fa fa-clipboard-list spc--right--sml k-custom-clickable',
						onClick: () => this.openModal({ component: CloseOutPalletizing }),
						authorization: 'warehouseSection.full',
						text: 'CLOSEOUTPALLETIZING',
					},
					{
						className: 'btn btn--dropdown header__button',
						iconClassName: 'fa fa-sign-out-alt spc--right--sml k-custom-clickable',
						onClick: () => this.openModal({ component: CloseOutExit }),
						authorization: 'warehouseSection.full',
						text: 'CLOSEOUTEXITSCAN',
					}
				);
			} else {
				buttons.buttons.push(
					{
						className: 'btn btn--dropdown header__button',
						iconClassName: 'fa fa-clipboard-list spc--right--sml k-custom-clickable',
						onClick: () => this.openConfirmationModal(this.closeOutPalletizingConfirmModalProps()),
						authorization: 'warehouseSection.full',
						text: 'CLOSEOUTPALLETIZING',
					},
					{
						className: 'btn btn--dropdown header__button',
						iconClassName: 'fa fa-sign-out-alt spc--right--sml k-custom-clickable',
						onClick: () => this.openConfirmationModal(this.closeOutExitScanConfirmModalProps()),
						authorization: 'warehouseSection.full',
						text: 'CLOSEOUTEXITSCAN',
					}
				);
			}
		}
		this.setButtonComponent(buttons);
	};

	@action.bound closePopup = () => {
		if (this.isBulkActionPopupOpen) {
			this.isBulkActionPopupOpen = false;
		}
	};

	@action.bound toggleBulkMenu = () => {
		this.isBulkActionPopupOpen = !this.isBulkActionPopupOpen;
	};

	@action.bound
	findFulfillmentOrders = async (willModalOpen = false) => {
		this.suspend();
		this.selectAll = false;
		this.isSelectAllCheckboxSelected = false;
		this.selectedItems = [];
		this.selectedGroups = [];
		if (this.baasicTableSettingsViewStore == undefined) {
			this.baasicTableSettingsViewStore = new BaasicTableSettingsViewStore(this.rootStore);
		}
		await this.baasicTableSettingsViewStore.setTemplates(
			this.tableData,
			'FulfillmentOrdersTableSettings',
			this.setAsChanged,
			this.findFulfillmentOrders
		);
		if (isSome(this.rootStore.viewStore.setModeMenu)) {
			this.rootStore.viewStore.setModeMenu(false);
		}
		let filter = {
			showTodayShipments: this.showTodayShipments,
			//isSKUInactive: true,
			includePii: true,
		};
		if (this.isPickingMode) {
			filter.statusIds = [
				_.find(this.lookupStore.data, item => {
					return item.abrv === fulfillmentOrderStatus.assigned;
				}).id,
			];
			filter.isNotOnHold = true;
		} else if (this.isBulkPrintMode) {
			filter.statusIds = _.map(
				_.filter(this.lookupStore.data, item => {
					return item.abrv === fulfillmentOrderStatus.assigned || item.abrv === fulfillmentOrderStatus.picking;
				}),
				item => item.id
			);
			filter.isNotOnHold = true;
			filter.isWarehouseDeleted = false;
		} else {
			filter.statusIds = null;
			filter.isNotOnHold = false;
			filter.isWarehouseDeleted = null;
			filter.warehouseId = null;
			if (_.get(this.warehouseDropdownStore, 'value')) this.warehouseDropdownStore.clear();
		}
		this.queryUtility.setFilterParams(this, filter, this.tableFilter);
		if (
			this.isPickingMode
				? this.isSeller || this.isSuperAdminOrganization
					? this.warehouseDropdownStore.value != null
					: true
				: true
		) {
			await this.listStore.find(this.queryUtility.filter);
			if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
				await this.queryUtility.changePage(1);
			}
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.checkAndHandleGrouping();
		this.queryUtility.setFilterPersistance(
			this,
			'fulfillment-orders-filter-filter',
			'fulfillment-orders-filter-table-data'
		);
		// Grouping persistance.
		localStorageHandler.set('groupByOption', this.groupByDropdownStore.value);
		this.setButtons();
		this.resume();
		if (typeof willModalOpen === 'boolean' && willModalOpen) {
			this.openPrintPdfModal();
		}
	};

	@action.bound
	onSelectGroupCheckbox(dataItem) {
		const data = this.listStore.response.item.slice();
		dataItem.isChecked = !dataItem.isChecked;
		dataItem.isPartiallyChecked = false;

		let orders = [];
		//Get all orders from selected group
		_.forEach(data, group => {
			if (group.value == dataItem.value) {
				_.forEach(group.items, item => {
					orders.push(item);
				});
			}
		});
		if (dataItem.isChecked) {
			//Mark as selected all orders inside group, add them to selected items array and add that group to the selected group array
			_.mapValues(orders, item => (item.isChecked = true));
			const eligibleOrders = _.filter(orders, order => {
				return !isSome(_.find(this.selectedItems, selectedItem => selectedItem.id == order.id));
			});
			this.selectedItems = _.concat(this.selectedItems, eligibleOrders);
			if (!isSome(_.find(this.selectedGroups, selectedGroup => selectedGroup.value == dataItem.value))) {
				this.selectedGroups.push(dataItem);
			}
		} else {
			//Mark all orders as unselected inside group and remove that group from selected group array
			_.mapValues(orders, item => (item.isChecked = false));

			_.remove(this.selectedItems, item => _.indexOf(orders, item) >= 0);

			_.remove(this.selectedGroups, item => item.value == dataItem.value);

			//If all orders are unselected set select all checkbox to unchecked
			if (this.selectedItems == null || this.selectedItems.length == 0) {
				this.isSelectAllCheckboxSelected = false;
			}
		}
		//Check if all groups are checked in order to set select all checkbox to right value
		const checkedItems = _.filter(data, group => group.isChecked == true);
		if (checkedItems.length == data.length) {
			this.isSelectAllCheckboxSelected = true;
		} else {
			this.isSelectAllCheckboxSelected = false;
		}
	}

	@action.bound
	checkAndHandleGrouping() {
		if (_.get(this.groupByDropdownStore, 'value') != null) {
			let tempData = [];
			if (this.groupByDropdownStore.value.value === 'productSku') {
				let multipleSkuOrders = [];
				_.forEach(this.listStore.response.item, item => {
					if (item.orderedItemCount > 1) {
						item.productSkus = '';
						// If multiple product SKU's are associated with the FO, set first element (which is used as primary 'group by' criteria) as empty.
						// That way the FO will be put in the '-' group which is translated as 'Multiple'.
						multipleSkuOrders.push(item);
					}
				});
				// Group by first element (sku) in the fulfillmentOrderItems.
				tempData = _.groupBy(this.listStore.response.item, 'productSkus');
			} else if (this.groupByDropdownStore.value.value === 'fulfillmentOrderShipByLocalDate') {
				// Group by date portion of the fulfillmentOrderShipByLocalDate property, excluding time portion.
				tempData = _.groupBy(this.listStore.response.item, item => {
					return moment(item.fulfillmentOrderShipByLocalDate).format('LL');
				});
			} else {
				tempData = _.groupBy(this.listStore.response.item, this.groupByDropdownStore.value.value);
			}
			const groupedData = _.map(tempData, (item, key) => {
				if (key === 'undefined' || key === 'null' || key === '') {
					if (this.groupByDropdownStore.value.value === 'productSku') {
						key = 'Multiple';
					} else {
						key = '-';
					}
				} else if (
					moment(key).isValid() &&
					this.groupByDropdownStore.value.value == 'fulfillmentOrderShipByLocalDate'
				) {
					key = moment(key).format('l');
				}
				return {
					field: this.groupByDropdownStore.value.value,
					items: item,
					value: key,
					expanded: false,
					isChecked: false,
					isPartiallyChecked: false,
					showSelectCheckbox: this.showSelectAllColumn,
					onSelectGroupCheckbox: this.onSelectGroupCheckbox,
				};
			});
			this.group = groupBy(this.listStore.response.item, [{ field: this.groupByDropdownStore.value.value }]);
			let sortedGroups = null;
			if (this.expandedGroups.length != 0) {
				const filteredData = _.forEach(groupedData, group => {
					if (
						_.find(this.expandedGroups, expandedGroup => {
							return group.value === expandedGroup.value;
						})
					) {
						group.expanded = true;
					}
				});
				// Group without value will always be sorted as the last group in the array, others ascending.
				sortedGroups = _.sortBy(filteredData, data => (data.value === '-' ? false : data.value));
			} else {
				// Group without value will always be sorted as the last group in the array, others ascending.
				sortedGroups = _.sortBy(groupedData, data => (data.value === '-' ? false : data.value));
			}
			this.listStore.setResponseItem(sortedGroups);
		}
	}

	@action.bound
	openPrintPdfModal() {
		if (this.pdfURL != null && this.printPdf) {
			setTimeout(function() {
				const frame = window.frames['pdf-print'];
				this.printPdf = false;
				frame.print();
			}, 250);
		}
	}

	@action.bound
	setRegularMode() {
		this.isPickingMode = false;
		this.isBulkPrintMode = false;
		this.selectedGroupByOption = null;
		const fulfillmentOrderStatusIdx = _.findIndex(
			this.tableData.columns,
			column => column.key === 'fulfillmentOrderStatus'
		);
		const assignedWarehouseIdx = _.findIndex(this.tableData.columns, column => column.key === 'warehouse');
		if (fulfillmentOrderStatusIdx > -1) {
			this.tableData.columns[fulfillmentOrderStatusIdx].filterType = TableFilterTemplate;
		}
		if (assignedWarehouseIdx > -1) {
			this.tableData.columns[assignedWarehouseIdx].filterType = TableFilterTemplate;
		}
		this.queryUtility.filter.warehouseId = null;
		this.groupByDropdownStore.setValue(localStorageHandler.get('groupByOption'));
		this.setHeaderComponent();
		this.findFulfillmentOrders();
	}

	@action.bound
	setPickingMode = async () => {
		this.suspend();
		this.isPickingMode = true;
		await this.setBulkPrintMode(false);
		this.groupByDropdownStore.value = null;
		this.isSelectAllCheckboxSelected = false;
		this.resume();
	};

	@action.bound
	setBulkPrintMode = async bulkPrintMode => {
		this.isBulkPrintMode = bulkPrintMode;
		_.find(this.tableData.columns, column => {
			return column.key == 'fulfillmentOrderStatus';
		}).filterApiParams.bulkPrintMode = bulkPrintMode;
		if (bulkPrintMode) {
			this.actions = null;
		}
		await this.prepareDataForMode();
		await this.setButtons();
	};

	@action.bound
	async prepareDataForMode() {
		this.groupByDropdownStore.value = localStorageHandler.get('groupByOption');
		_.each(this.tableData.columns.filter(c => c.filter != null), column => {
			column.filter = null;
		});
		if (this.queryUtility.filter.advancedSearch != null) {
			delete this.queryUtility.filter.advancedSearch;
		}
		this.selectedItems = [];
		this.selectedGroups = [];
		this.pdfURL = null;
		if ((this.isSeller || this.isSuperAdminOrganization) && this.isPickingMode) {
			await this.warehouseDropdownStore.reload();
			this.queryUtility.recordCount = 0;
			this.listStore.setResponseItem([]);
			if (this.warehouseDropdownStore.value) {
				await this.findFulfillmentOrders();
			}
		} else {
			await this.findFulfillmentOrders();
		}
	}

	@action.bound
	onWarehouseSelect = selectedItem => {
		if (!selectedItem) return;
		this.selectedItems = [];
		this.selectedGroups = [];
		this.queryUtility.filter.warehouseId = selectedItem.value;
		this.findFulfillmentOrders();
	};

	@action.bound
	cancelFulfillmentOrder = async item => {
		this.suspend();
		try {
			await this.fulfillmentOrderStore.cancelFulfillmentOrder(item, sourceOfChange.cancelFulfillmentOrderList);
			this.rootStore.notificationStore.success(['ORDERVIEW.FULFILLMENTORDERCANCELED', { id: item.fulfillmentOrderId }]);
			await this.queryUtility.initialize(false);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	createPickingBatch = async () => {
		if (this.selectedItems.length === 0) {
			this.rootStore.notificationStore.error('ORDERVIEW.NOITEMSSELECTED');
			return;
		}
		this.suspend();
		let exception = false;
		const pdfData = this.preparePdF();
		const resource = {
			fulfillmentOrderIds: _.map(this.selectedItems, p => p.id),
			pdfData: { pickingBatchPdfItems: pdfData },
		};
		try {
			const response = await this.pickingBatchStore.create(resource, sourceOfChange.pickingBatchCreate);
			this.selectedItems = [];
			this.selectedGroups = [];
			await this.pickingBatchStore.generatePdf(response.id);
			const pdfResponse = this.pickingBatchStore.response;
			const fileURL = createFileUrl(pdfResponse.pdfData);
			printJS(fileURL);
			this.rootStore.notificationStore.success([
				'FULFILLMENTORDER.PICKINGBATCH.SUCCESS',
				{ pickingBatchId: response.pickingBatchId },
			]);
			this.findFulfillmentOrders();
		} catch (err) {
			exception = true;
			this.rootStore.notificationStore.error(['FULFILLMENTORDER.PICKINGBATCH.ERROR']);
		}
		this.resume();
	};

	@action.bound
	preparePdF = () => {
		const dataArray = [];
		_.each(this.selectedItems, fulfillmentOrder => {
			_.each(fulfillmentOrder.fulfillmentOrderItems, fulfillmentOrderItem => {
				const productData = fulfillmentOrderItem.product;
				const dataItem = {
					product: {
						image: productData.image,
						sku: fulfillmentOrderItem.sku,
						name: productData.name,
					},
					orderNumber: fulfillmentOrder.order.orderNumber,
				};

				dataArray.push(dataItem);
			});
		});
		const pdfData = [];
		const groupedData = _.groupBy(dataArray, 'product.sku');
		_.each(Object.keys(groupedData), key => {
			const ordersList = _.map(groupedData[key], item => item.orderNumber);
			const pdfDataItem = {
				product: { ...groupedData[key][0].product, required: ordersList.length, image: tempImagePlaceholder.source },
				orders: _.join(_.uniq(ordersList), ' | '),
			};

			pdfData.push(pdfDataItem);
		});

		return pdfData;
	};

	@action.bound
	closeOutPalletizing = async () => {
		this.suspend();
		try {
			const response = await this.warehouseOrganizationInfoStore.closeOutPalletizing(
				sourceOfChange.closeOutPalletizing
			);
			if (response) {
				this.rootStore.notificationStore.success('WAREHOUSEVIEW.CLOSEOUT.PALLETIZING.SUCCESS');
				await this.queryUtility.initialize(false);
			} else {
				this.rootStore.notificationStore.error('WAREHOUSEVIEW.CLOSEOUT.PALLETIZING.FAILURE');
				this.setCloseOutFlag(true);
				this.rootStore.routerStore.goTo('master.app.main.order.closeout.detail');
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	onSelectAllCheckbox = () => {
		this.isSelectAllCheckboxSelected = !this.isSelectAllCheckboxSelected;
		const data = this.listStore.response.item.slice();
		if (this.groupByDropdownStore.value) {
			// If data is grouped.
			let orders = [];
			_.forEach(data, group => {
				//Setting all groups selection to the select all checkbox value
				group.isChecked = this.isSelectAllCheckboxSelected;
				group.isPartiallyChecked = false;

				if (group.isChecked) {
					//If group is selected, add it to selected groups array, if it's not already added
					if (!isSome(_.find(this.selectedGroups, selectedGroup => selectedGroup.value == group.value))) {
						this.selectedGroups.push(group);
					}
				} else {
					//If group is unselected, remove it from selected groups array if it exists
					_.remove(this.selectedGroups, selectedGroup => selectedGroup.value == group.value);
				}
				_.forEach(group.items, item => {
					orders.push(item);
				});
			});
			if (this.isSelectAllCheckboxSelected) {
				_.forEach(orders, item => {
					item.isChecked = true;
				});
				this.selectedItems = orders;
			} else {
				_.forEach(orders, item => {
					item.isChecked = false;
				});
				this.selectedItems = [];
			}
		} else {
			// If data is not grouped.
			if (this.isSelectAllCheckboxSelected) {
				_.forEach(data, item => {
					item.isChecked = true;
				});
				this.selectedItems = data;
			} else {
				_.forEach(data, item => {
					item.isChecked = false;
				});
				this.selectedItems = [];
			}
		}
	};

	@action.bound
	onSelectFulfillmentOrderCheckbox = id => {
		const data = this.listStore.response.item.slice();
		if (this.groupByDropdownStore.value) {
			// If data is grouped.
			let fOrders = [];
			_.forEach(data, group => {
				_.forEach(group.items, item => {
					fOrders.push(item); // Fills the list with all fulfillment orders from the grouped data.
				});
			});
			const itemIndex = _.findIndex(fOrders, item => {
				return item.id === id;
			});
			fOrders[itemIndex].isChecked = !fOrders[itemIndex].isChecked;
			this.selectedItems = _.filter(fOrders, fOrder => fOrder.isChecked).slice();
			if (this.selectedItems.length !== fOrders.length) {
				this.isSelectAllCheckboxSelected = false;
			} else {
				this.isSelectAllCheckboxSelected = true;
			}
			//Find group to which certain order belongs
			let itemGroup = _.find(data, group => {
				return _.find(group.items, item => {
					return item.id == id;
				});
			});
			const checkedItems = _.filter(itemGroup.items, item => item.isChecked == true);
			//If all items inside that group are selected, mark that group as selected and add it to selected groups array
			if (checkedItems.length == itemGroup.items.length) {
				itemGroup.isChecked = true;
				itemGroup.isPartiallyChecked = false;
				if (!isSome(_.find(this.selectedGroups, selectedGroup => selectedGroup.value == itemGroup.value))) {
					this.selectedGroups.push(itemGroup);
				}
			} else {
				//If all items are not selected mark that group as unselected and remove it from selected groups array if exists
				itemGroup.isChecked = false;
				if (checkedItems.length > 0) {
					itemGroup.isPartiallyChecked = true;
				} else {
					itemGroup.isPartiallyChecked = false;
				}
				_.remove(this.selectedGroups, selectedGroup => selectedGroup.value == itemGroup.value);
			}
		} else {
			// If data is not grouped.
			const itemIndex = _.findIndex(data, item => {
				return item.id === id;
			});
			data[itemIndex].isChecked = !data[itemIndex].isChecked;
			this.selectedItems = _.filter(data, item => item.isChecked);
			if (this.selectedItems.length !== data.length) {
				this.isSelectAllCheckboxSelected = false;
			} else {
				this.isSelectAllCheckboxSelected = true;
			}
		}
	};

	@action.bound
	onGroupBySelect = async selectedValue => {
		if (this.selectedValue != null) {
			this.queryUtility.filter.isGroupBy = true;
			this.queryUtility.filter.groupBySort = selectedValue.value + '|asc';
		} else {
			this.queryUtility.filter.isGroupBy = false;
			this.queryUtility.filter.groupBySort = '';
			this.group = null;
		}
		await this.findFulfillmentOrders();
	};

	@action.bound
	expandChange = evt => {
		evt.dataItem.expanded = evt.value;
		if (evt.dataItem.expanded) {
			this.expandedGroups.push(evt.dataItem);
		} else {
			this.expandedGroups = _.filter(this.expandedGroups, expandedGroup => {
				return expandedGroup.expanded && expandedGroup.value !== evt.dataItem.value;
			});
		}
		this.setAsChanged();
	};

	@action.bound
	async closeOutExitScan() {
		this.suspend();
		try {
			const response = await this.warehouseOrganizationInfoStore.closeOutExitScan(sourceOfChange.closeOutExitScan);
			if (response) {
				this.rootStore.notificationStore.success('WAREHOUSEVIEW.CLOSEOUT.EXITSCAN.SUCCESS');
				await this.queryUtility.initialize(false);
			} else {
				this.rootStore.notificationStore.error('WAREHOUSEVIEW.CLOSEOUT.EXITSCAN.FAILURE');
				this.setCloseOutFlag(false);
				this.rootStore.routerStore.goTo('master.app.main.order.closeout.detail');
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}

	setCloseOutFlag = flag => {
		localStorageHandler.set('isCloseOutPalletizing', flag);
	};

	bulkPrintHistoryCallback = ({ pdfURL, printPdf }) => {
		this.pdfURL = pdfURL;
		this.printPdf = printPdf;
		setTimeout(() => {
			this.openPrintPdfModal();
		}, 500);
	};

	openChangeWarehouseShipByDateModal() {
		const selectedItems = _.filter(this.selectedItems, selectedItem => {
			return selectedItem.fulfillmentOrderStatusAbrv != fulfillmentOrderStatus.exitScan;
		});
		if (selectedItems.length > 0) {
			this.openModal({
				onSuccess: async () => await this.findFulfillmentOrders(),
				data: selectedItems,
				component: ChangeWarehouseShipByDate,
			});
		} else {
			this.rootStore.notificationStore.error('CHANGEWAREHOUSESHIPBYDATE.NOVALIDORDERSELECTED');
		}
	}

	openBulkMarkAsShipped() {
		const selectedItemIds = _.filter(this.selectedItems, selectedItem => {
			const index = _.findIndex(onMarkAsShippedStatuses, item => item == selectedItem.fulfillmentOrderStatusAbrv);
			if (index != -1) {
				return selectedItem.id;
			}
		});
		if (selectedItemIds.length == 0) {
			this.rootStore.notificationStore.error(['ORDERVIEW.NOVALIDORDERSELECTEDFORMARKSASSHIPPED']);
			// If some of the orders are in statuses which do not allow for mark as shipped
		} else if (selectedItemIds.length > 0 && selectedItemIds.length != this.selectedItems.length) {
			this.rootStore.notificationStore.error(['ORDERVIEW.SOMEORDERSAREINVALIDFORMARKASSHIPPED']);
		} else {
			this.openModal({
				onClose: () => this.resetSelectedItems(),
				onSuccess: async () => await this.findFulfillmentOrders(),
				data: this.selectedItems,
				component: MarkAsShippedBulk,
				className: 'ReactModal--xlrg',
			});
		}
	}

	openBulkOnHoldModal() {
		const selectedItems = _.filter(this.selectedItems, selectedItem => {
			let index = _.findIndex(onHoldStatuses, item => item == selectedItem.fulfillmentOrderStatusAbrv);
			if (index != -1) {
				return selectedItem;
			}
		});
		if (selectedItems.length > 0) {
			this.openModal({
				onClose: () => this.resetSelectedItems(),
				onSuccess: async () => await this.queryUtility.initialize(false),
				data: { selectedItemIds: this.selectedItems.map(item => item.id) },
				component: OnHold,
			});
		} else {
			this.rootStore.notificationStore.error(['ONHOLDVIEW.NOVALIDORDERSELECTED']);
		}
	}

	getBulkPullLabelConfirmModalProps = items => {
		return {
			onConfirm: () => this.pullLabelBulk(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['ORDERVIEW.BULKPULLLABEL', { count: items.length }],
			data: items,
		};
	};

	@action.bound
	openBulkPullLabelConfirmModal() {
		let selectedItems = [];
		selectedItems = _.filter(this.selectedItems, selectedItem => {
			let index = _.findIndex(onPullLabelStatuses, status => status == selectedItem.fulfillmentOrderStatusAbrv);
			if (index != -1) {
				// Find if selected order has active label.
				let hasActiveLabel = isSome(selectedItem.activeShipmentCount) && selectedItem.activeShipmentCount > 0;
				if (!hasActiveLabel) {
					return selectedItem;
				}
			}
		});
		if (selectedItems.length > 0) {
			this.openConfirmationModal(this.getBulkPullLabelConfirmModalProps(this.selectedItems));
		} else {
			this.rootStore.notificationStore.error(['ORDERVIEW.BULKNOVALIDORDERSELECTEDFORPULLLABEL']);
		}
	}

	@action.bound
	openVoidActiveLabelConfirmModal = item => {
		if (this.selectedItems.length == 0) {
			if (!this.plainLabelVoiding && item.isBuyShipping) {
				this.openConfirmationModal(
					this.getVoidLabelConfirmModalProps(item, [
						'ORDERVIEW.VOIDLABELONCHANNEL',
						{ fulfillmentOrderId: item.fulfillmentOrderId },
					])
				);
			} else if (this.plainLabelVoiding) {
				if (item.isBuyShipping) {
					this.openModal({
						onSuccess: async () => await this.findFulfillmentOrders(),
						data: { selectedItemId: item.id },
						component: VoidLabel,
					});
				} else {
					this.openConfirmationModal(
						this.getVoidLabelConfirmModalProps(item, [
							'ORDERVIEW.VOIDLABEL',
							{ fulfillmentOrderId: item.fulfillmentOrderId },
						])
					);
				}
			}
		} else {
			this.openConfirmationModal(this.getVoidLabelConfirmModalProps(item, 'ORDERVIEW.VOIDALLLABELS'));
		}
	};

	@action.bound
	openVoidLabelConfirmModal(item) {
		this.plainLabelVoiding = true;
		this.openVoidActiveLabelConfirmModal(item);
	}

	@action.bound
	openVoidChannelLabelsModal = selectedItem => {
		let hasActiveShipment = isSome(selectedItem.activeShipmentCount) && selectedItem.activeShipmentCount > 0;
		if (
			selectedItem.isBuyShipping &&
			hasActiveShipment &&
			!(
				selectedItem.fulfillmentOrderStatusAbrv == fulfillmentOrderStatus.shippedExternally ||
				selectedItem.fulfillmentOrderStatusAbrv == fulfillmentOrderStatus.exitScan
			)
		) {
			return true;
		}
		return false;
	};

	pullLabelProductionModeConfirmModalProps = item => {
		return {
			onConfirm: () => this.pullLabel(this.rootStore.viewStore.selectedConfirmModal.data),
			message: 'PRODUCTIONPULLLABEL',
			data: item,
		};
	};

	@action.bound
	async openChangeShippingModalBulk() {
		const selectedItems = _.filter(this.selectedItems, selectedItem => {
			const index = _.findIndex(onChangeShippingStatuses, item => item == selectedItem.fulfillmentOrderStatusAbrv);
			if (index != -1 && selectedItem.orderChannelType !== channelType.amazonVendor) {
				return selectedItem;
			}
		});
		if (selectedItems.length > 0) {
			this.openModal({
				onClose: () => this.resetSelectedItems(),
				onSuccess: async () => await this.findFulfillmentOrders(),
				data: { selectedItemIds: this.selectedItems.map(item => item.id) },
				component: ChangeShipping,
			});
		} else {
			this.rootStore.notificationStore.error(['CHANGESHIPPINGVIEW.NOVALIDORDERSELECTED']);
		}
	}

	@action.bound
	async openAssignModalBulk() {
		const filteredSelectedItems = _.filter(this.selectedItems, selectedItem => {
			let index = _.findIndex(onAssignStatuses, item => item == selectedItem.fulfillmentOrderStatusAbrv);
			if (index != -1) {
				return selectedItem;
			}
		});
		if (filteredSelectedItems.length > 0) {
			this.openModal({
				...this.assignProps,
				data: { selectedItemIds: this.selectedItems.map(item => item.id) },
			});
		} else {
			this.rootStore.notificationStore.error(['ORDERVIEW.NOVALIDORDERSELECTEDFORASSIGN']);
		}
	}

	@action.bound
	openSplitFulfillmentOrderModal = item => {
		const hasLabel =
			(isSome(item.activeShipmentCount) && item.activeShipmentCount > 0 && isSome(orderItem.label)) ||
			(item.fulfillmentOrderStatusAbrv == fulfillmentOrderStatus.shippedExternally ||
				item.fulfillmentOrderStatusAbrv == fulfillmentOrderStatus.exitScan);
		this.openModal({
			onSuccess: async () => await this.findFulfillmentOrders(),
			data: {
				selectedItemId: item.id,
				orderId: item.orderId,
				isEdit: !hasLabel && (item.isSplit || (item.isCreatedBySplit && item.originalFulfillmentOrderIdInt)),
			},
			component: SplitFulfillmentOrder,
			className: 'ReactModal--lrg',
		});
	};

	@action.bound
	openReoptimizeConfirmModalBulk = () => {
		const selectedItems = _.filter(this.selectedItems, selectedItem => {
			const index = _.findIndex(onReoptimizeStatuses, item => item == selectedItem.fulfillmentOrderStatusAbrv);
			if (index != -1) {
				return selectedItem;
			}
		});
		if (selectedItems.length == this.selectedItems.length) {
			this.openConfirmationModal(this.getReoptimizeBulkConfirmModalProps());
		} else {
			this.rootStore.notificationStore.error(['ORDERVIEW.REOPTIMIZEORDERBULKERROR']);
		}
	};

	@action.bound
	openUpdateBuyShippingModalBulk = () => {
		const selectedItems = _.filter(this.selectedItems, selectedItem => {
			const index = _.findIndex(onUpdateBuyShippingStatuses, item => item == selectedItem.fulfillmentOrderStatusAbrv);
			if (index != -1 && selectedItem.orderChannelType == channelType.amazon) {
				return selectedItem;
			}
		});
		if (selectedItems.length > 0) {
			this.openModal({
				onClose: () => this.resetSelectedItems(),
				onSuccess: async () => await this.findFulfillmentOrders(),
				data: this.selectedItems,
				component: UpdateBuyShipping,
			});
		} else {
			this.rootStore.notificationStore.error(['UPDATEBUYSHIPPINGVIEW.NOVALIDORDERSELECTED']);
		}
	};

	@action.bound
	openNeverShippedModalBulk() {
		const filteredSelectedItems = _.filter(this.selectedItems, selectedItem => {
			let index = _.findIndex(onNeverShippedStatuses, item => item == selectedItem.fulfillmentOrderStatusAbrv);
			if (index != -1 && selectedItem.orderChannelType !== channelType.amazonVendor) {
				return selectedItem;
			}
		});

		if (filteredSelectedItems.length == 0) {
			this.rootStore.notificationStore.error(['ORDERVIEW.NOVALIDORDERSELECTEDFORNEVERSHIPPED']);
			// If some of the orders are in statuses which do not allow for mark as never shipped
		} else if (filteredSelectedItems.length > 0 && filteredSelectedItems.length != this.selectedItems.length) {
			this.rootStore.notificationStore.error(['ORDERVIEW.SOMEORDERSAREINVALIDFORMARKASNEVERSHIPPED']);
		} else {
			this.openModal({
				...this.neverShippedProps,
				data: { selectedItems: this.selectedItems },
			});
		}
	}

	assignProps = {
		onClose: () => this.resetSelectedItems(),
		onSuccess: async () => await this.findFulfillmentOrders(),
		component: AssignCarrierServiceAndWarehouse,
	};

	neverShippedProps = {
		onClose: () => this.resetSelectedItems(),
		onSuccess: async () => await this.findFulfillmentOrders(),
		component: NeverShipped,
	};

	@action.bound
	onCheck = async check => {
		this.selectedItems = [];
		this.selectedGroups = [];
		if (check) {
			this.showTodayShipments = check.currentTarget.checked;
			this.orderDateRangeViewStore.todayFilterChange(this.showTodayShipments);
			if (!this.isPickingMode) {
				this.findFulfillmentOrders();
			} else {
				if (this.isSeller || this.isSuperAdminOrganization ? this.warehouseDropdownStore.value != null : true) {
					this.findFulfillmentOrders();
				}
			}
		}
	};

	@action.bound
	onWarehouseSettingCheck = async check => {
		if (check) {
			this.useRegularPalletizingMode = check.currentTarget.checked;
			let warehouseOrgInfo = _.cloneDeep(this.organizationStore.item.warehouseOrganizationInfo);
			warehouseOrgInfo.useRegularPalletizingMode = this.useRegularPalletizingMode;
			await this.warehouseOrganizationInfoStore.updateWarehouseOrganizationInfo(
				warehouseOrgInfo,
				sourceOfChange.updateWarehouseOrganizationInfo
			);
			this.setModes();
		}
	};

	getCancelConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.cancelFulfillmentOrder(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['ORDERVIEW.CANCELFULFILLMENTORDER', { id: item.fulfillmentOrderId }],
			data: item,
		};
	};

	getPullLabelConfirmModalProps = item => {
		return {
			onConfirm: () => {
				if (
					(process.env.NODE_ENV === 'staging' ||
						process.env.NODE_ENV == 'test' ||
						process.env.NODE_ENV == 'development') &&
					this.rootStore.viewStore.selectedConfirmModal.data.productionMode
				) {
					this.openConfirmationModal(
						this.pullLabelProductionModeConfirmModalProps(this.rootStore.viewStore.selectedConfirmModal.data)
					);
				} else {
					this.pullLabel(this.rootStore.viewStore.selectedConfirmModal.data);
				}
			},
			message: ['ORDERVIEW.PULLLABELCONFIRM', { id: item.fulfillmentOrderId }],
			data: item,
		};
	};

	closeOutPalletizingConfirmModalProps = () => {
		return {
			onConfirm: async () => await this.closeOutPalletizing(),
			message: 'ORDERVIEW.CLOSEOUTPALLETIZING',
		};
	};

	closeOutExitScanConfirmModalProps = () => {
		return {
			onConfirm: async () => await this.closeOutExitScan(),
			message: 'ORDERVIEW.CLOSEOUTEXITSCAN',
		};
	};

	getVoidLabelConfirmModalProps = (item, message) => {
		return {
			onConfirm: async () => {
				this.suspend();
				if (!this.plainLabelVoiding) {
					if (this.selectedItems.length == 0) {
						if (this.reoptimizeLabelVoiding) {
							await this.reoptimizeOrder(this.rootStore.viewStore.selectedConfirmModal.data, true);
							this.reoptimizeLabelVoiding = false;
						}
					} else if (this.reoptimizeLabelVoiding) {
						await this.reoptimizeOrderBulk(this.selectedItems, true);
						this.reoptimizeLabelVoiding = false;
					}
				} else {
					this.plainLabelVoiding = false;
					let voidLabelOption = this.rootStore.viewStore.selectedConfirmModal.data.isBuyShipping ? 1 : 0;
					try {
						const response = await this.shipmentInfoStore.voidLabel(
							{ fulfillmentOrderId: this.rootStore.viewStore.selectedConfirmModal.data.id },
							voidLabelOption,
							sourceOfChange.voidActiveLabelConfirmModal
						);

						handleVoidLabelResponseNotification(
							this.rootStore,
							response,
							this.rootStore.viewStore.selectedConfirmModal.data.fulfillmentOrderId
						);
						await this.queryUtility.initialize(false);
					} catch (err) {
						notificationError({ store: this.rootStore.notificationStore, err });
					}
				}
				this.resume();
			},
			onCancel: async () => {
				if (!this.plainLabelVoiding) {
					if (this.selectedItems.length == 0) {
						if (this.reoptimizeLabelVoiding) {
							await this.reoptimizeOrder(this.rootStore.viewStore.selectedConfirmModal.data);
							this.reoptimizeLabelVoiding = false;
						}
					} else if (this.reoptimizeLabelVoiding) {
						await this.reoptimizeOrderBulk(this.selectedItems, false);
						this.reoptimizeLabelVoiding = false;
					}
				} else {
					this.plainLabelVoiding = false;
				}
			},
			message: message,
			data: item,
		};
	};

	getReoptimizeConfirmModalProps = item => {
		return {
			onConfirm: async () => {
				if (this.openVoidChannelLabelsModal(this.rootStore.viewStore.selectedConfirmModal.data)) {
					this.suspend();
					this.reoptimizeLabelVoiding = true;
					this.openVoidActiveLabelConfirmModal(this.rootStore.viewStore.selectedConfirmModal.data);
					this.resume();
				} else {
					await this.reoptimizeOrder(this.rootStore.viewStore.selectedConfirmModal.data);
				}
			},
			message: ['ORDERVIEW.REOPTIMIZEORDERCONFIRM', { id: item.fulfillmentOrderId }],
			data: item,
		};
	};

	getReoptimizeBulkConfirmModalProps = () => {
		return {
			onConfirm: async () => {
				this.suspend();
				this.reoptimizeLabelVoiding = true;
				if (this.rootStore.viewStore.selectedConfirmModal.data.some(o => this.openVoidChannelLabelsModal(o))) {
					this.openConfirmationModal(
						this.getVoidLabelConfirmModalProps(
							this.rootStore.viewStore.selectedConfirmModal.data,
							'ORDERVIEW.VOIDALLLABELS'
						)
					);
				} else {
					await this.reoptimizeOrderBulk(this.rootStore.viewStore.selectedConfirmModal.data);
				}
				this.resume();
			},
			message: ['ORDERVIEW.REOPTIMIZEORDERCONFIRMBULK', { count: this.selectedItems.length }],
			data: this.selectedItems,
		};
	};

	getEditOrderModalProps = item => {
		return {
			onSuccess: async () => await this.findFulfillmentOrders(),
			data: { parentView: this, selectedItemId: item.id },
			component: EditOrder,
		};
	};

	@action.bound
	resetSelectedItems = () => {
		this.selectedItems.forEach(o => delete o.isChecked);
		this.selectedItems = [];
		this.isSelectAllCheckboxSelected = false;
		if (this.groupByDropdownStore.value) {
			this.selectedGroups.forEach(g => delete g.isChecked);
			this.selectedGroups = [];
		}
	};
}

export default OrderListViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/OrderListViewStore.js